import React from "react";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { AnimatedNumber } from "./ui/animated-number";
import { Activity, Cpu, Zap, Clock, TrendingDown } from "lucide-react";

const StatsCards = ({ data, loading = false }) => {
  // 从data中提取数据，处理undefined情况
  const balance = data?.balance || 0;
  const usage = data?.usage || 0;
  const logs = data?.logs || [];

  // 计算统计数据
  const totalCalls = logs.length;
  const uniqueModels = [...new Set(logs.map((log) => log.model_name))].length;
  const totalTokensUsed = logs.reduce(
    (sum, log) => sum + (log.prompt_tokens || 0) + (log.completion_tokens || 0),
    0
  );
  const avgResponseTime =
    logs.length > 0
      ? logs.reduce((sum, log) => sum + (log.use_time || 0), 0) / logs.length
      : 0;

  const usagePercentage = balance > 0 ? (usage / balance) * 100 : 0;
  const remaining = balance - usage;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
      {/* 主卡片 - 令牌余额（移动端全宽，桌面端占8列） */}
      <div className="lg:col-span-8">
        <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
          <CardContent className="relative z-10 flex flex-col justify-center min-h-[180px] sm:min-h-[200px] lg:min-h-[240px] px-4 sm:px-6 py-6 sm:py-8">
            {/* 剩余额度显示 */}
            <div className="text-center space-y-3 sm:space-y-4">
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 font-medium">
                可用额度
              </p>
              <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 dark:text-gray-100 min-h-[2.5rem] sm:min-h-[3rem] lg:min-h-[4rem] flex items-center justify-center">
                {loading ? (
                  <Skeleton className="h-10 sm:h-12 lg:h-16 w-32 sm:w-40 lg:w-56 bg-gray-200 dark:bg-gray-700 rounded-2xl" />
                ) : balance === 0 && usage === 0 ? (
                  <span className="text-gray-400 dark:text-gray-500 text-base sm:text-lg lg:text-2xl">
                    请先查询数据
                  </span>
                ) : (
                  <span className="text-blue-600 dark:text-blue-400">
                    {balance === 100000000 ? (
                      "无限制"
                    ) : (
                      <AnimatedNumber
                        value={remaining}
                        prefix="$"
                        decimals={2}
                        duration={1.2}
                        className="text-blue-600 dark:text-blue-400 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold"
                      />
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* 使用进度 */}
            {balance !== 100000000 && !loading && balance > 0 && (
              <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400 font-medium text-xs sm:text-sm">
                    使用进度
                  </span>
                  <span className="font-bold text-gray-900 dark:text-gray-100 text-sm sm:text-base lg:text-lg">
                    <AnimatedNumber
                      value={usagePercentage}
                      suffix="%"
                      decimals={1}
                      duration={1}
                      className="font-bold"
                    />
                  </span>
                </div>
                <Progress
                  value={usagePercentage}
                  animationDuration={1.2}
                  className="h-2 sm:h-3 bg-gray-200 dark:bg-gray-700 rounded-full"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 右侧统计信息（移动端全宽，桌面端占4列） */}
      <div className="lg:col-span-4 space-y-4 sm:space-y-6">
        {/* 上半部分：Token和响应时间 */}
        <div className="grid grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4">
          <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-md hover:shadow-lg transition-all duration-300">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2 mb-2 sm:mb-0">
                  <div className="p-1.5 sm:p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <Zap className="h-3 w-3 sm:h-4 sm:w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium sm:hidden">
                    总消耗Tokens
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium hidden sm:inline">
                  总消耗Tokens
                </span>
              </div>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? (
                  <Skeleton className="h-4 sm:h-5 w-12 sm:w-14 lg:w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                ) : (
                  <span className="text-sm sm:text-base lg:text-lg">
                    <AnimatedNumber
                      value={totalTokensUsed || 0}
                      formatNumber={false}
                      duration={1.5}
                      className="font-bold"
                    />
                  </span>
                )}
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-md hover:shadow-lg transition-all duration-300">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2 mb-2 sm:mb-0">
                  <div className="p-1.5 sm:p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium sm:hidden">
                    平均响应
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium hidden sm:inline">
                  平均响应
                </span>
              </div>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? (
                  <Skeleton className="h-4 sm:h-5 w-10 sm:w-12 lg:w-14 bg-gray-200 dark:bg-gray-700 rounded" />
                ) : (
                  <span className="text-sm sm:text-base lg:text-lg">
                    <AnimatedNumber
                      value={avgResponseTime || 0}
                      suffix="秒"
                      decimals={1}
                      duration={1}
                      className="font-bold"
                    />
                  </span>
                )}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* 下半部分：已用额度和总调用 */}
        <div className="grid grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4">
          <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-md hover:shadow-lg transition-all duration-300">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2 mb-2 sm:mb-0">
                  <div className="p-1.5 sm:p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium sm:hidden">
                    已用额度
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium hidden sm:inline">
                  已用额度
                </span>
              </div>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? (
                  <Skeleton className="h-4 sm:h-5 w-12 sm:w-14 lg:w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                ) : (
                  <span className="text-sm sm:text-base lg:text-lg">
                    <AnimatedNumber
                      value={usage}
                      prefix="$"
                      decimals={2}
                      duration={1}
                      className="font-bold"
                    />
                  </span>
                )}
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-md hover:shadow-lg transition-all duration-300">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2 mb-2 sm:mb-0">
                  <div className="p-1.5 sm:p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium sm:hidden">
                    总调用次数
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium hidden sm:inline">
                  总调用次数
                </span>
              </div>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? (
                  <Skeleton className="h-4 sm:h-5 w-10 sm:w-12 lg:w-14 bg-gray-200 dark:bg-gray-700 rounded" />
                ) : (
                  <span className="text-sm sm:text-base lg:text-lg">
                    <AnimatedNumber
                      value={totalCalls}
                      formatNumber={false}
                      duration={1.3}
                      className="font-bold"
                    />
                  </span>
                )}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* 模型数 - 全宽显示 */}
        <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-md hover:shadow-lg transition-all duration-300">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-0">
                <div className="p-1.5 sm:p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                  <Cpu className="h-3 w-3 sm:h-4 sm:w-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">
                    已使用模型数
                  </p>
                  <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-gray-100">
                    {loading ? (
                      <Skeleton className="h-4 sm:h-5 w-6 sm:w-8 lg:w-10 bg-gray-200 dark:bg-gray-700 rounded" />
                    ) : (
                      <span className="text-sm sm:text-base lg:text-lg">
                        <AnimatedNumber
                          value={uniqueModels}
                          formatNumber={false}
                          duration={1}
                          className="font-bold"
                        />
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">
                共{" "}
                <AnimatedNumber
                  value={uniqueModels}
                  formatNumber={false}
                  duration={1}
                  className="font-medium"
                />{" "}
                种不同模型
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StatsCards;
